import { PlusOutlined } from "@ant-design/icons";
import { Popover, Space, Tag, Typography } from "antd";
import { Suspense, useState } from "react";
import { graphql, useFragment, useMutation } from "react-relay";

import CategorySelector from "components/common/annotations/CategorySelector";
import { useConnectionNodes } from "components/common/Pagination";
import {
  createAnnotation as createAnnotationMutation,
  deleteAnnotation as deleteAnnotationMutation,
} from "hooks/mutation/annotation";
import { displayMutationResults } from "utils/mutations";

import type { FC } from "react";
import type { annotationCreateAnnotationMutation } from "__generated__/annotationCreateAnnotationMutation.graphql";
import type { annotationDeleteAnnotationMutation } from "__generated__/annotationDeleteAnnotationMutation.graphql";
import type {
  FlightAnnotations_flight$data,
  FlightAnnotations_flight$key,
} from "__generated__/FlightAnnotations_flight.graphql";
import type { ConnectionNodeType } from "components/common/Pagination";

export interface Props {
  flightRef: FlightAnnotations_flight$key;
}

type Annotation = ConnectionNodeType<FlightAnnotations_flight$data["annotations"]>;

const CLOUD_WORKER_USER_ID = "93626f58-7ab7-47cc-8cbc-f5c2970cf744";

const isSystemAnnotation = (a: Annotation) => {
  return a.authorId === null || a.authorId === CLOUD_WORKER_USER_ID;
};

const FlightAnnotations: FC<Props> = ({ flightRef }) => {
  const flight = useFragment(
    graphql`
      fragment FlightAnnotations_flight on Flight {
        uuid
        organization {
          uuid
        }
        annotations {
          __id
          edges {
            node {
              id
              authorId
              author {
                displayName
              }
              flightDataFileId
              internalMetadata
              annotationCategory {
                id
                uuid
                name
                color
                isCustomerFacing
              }
            }
          }
        }
      }
    `,
    flightRef
  );

  const [createAnnotation] =
    useMutation<annotationCreateAnnotationMutation>(createAnnotationMutation);
  const [deleteAnnotation] =
    useMutation<annotationDeleteAnnotationMutation>(deleteAnnotationMutation);

  // Only show annotations that are directly on the flight, exclude annotations directly on media of
  // the flight.
  // TODO(ryanr) - Move this to the backend and filter at the graphql query layer
  const annotations = useConnectionNodes(flight.annotations).filter(
    a => a.flightDataFileId === null
  );
  const userFacingAnnotations = annotations.filter(a => !isSystemAnnotation(a));
  const systemAnnotations = annotations.filter(isSystemAnnotation);
  const [inputVisible, setInputVisible] = useState(false);

  const showInput = (event: React.MouseEvent<HTMLSpanElement>) => {
    setInputVisible(true);
    event.stopPropagation();
  };

  const onCategoriesSelected = (categoryUuids: string[]) => {
    const currentAnnotationUuids = annotations.map(a => a.annotationCategory?.uuid || "");
    const categoryUuidsToAdd = categoryUuids.filter(id => !currentAnnotationUuids.includes(id));
    categoryUuidsToAdd.forEach(categoryUuid => {
      createAnnotation({
        variables: {
          flightId: flight.uuid,
          annotationCategoryId: categoryUuid,
          connections: [flight.annotations?.__id || ""],
        },
        onCompleted: displayMutationResults(response => response.createAnnotation, {}),
      });
    });
    setInputVisible(false);
  };

  const tagPlusStyle: React.CSSProperties = {
    borderStyle: "dashed",
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} onClick={e => e.stopPropagation()}>
      {!inputVisible ? (
        <Space size={[0, 8]} wrap>
          {userFacingAnnotations.map(annotation => {
            const tag = annotation.annotationCategory?.name || "";

            const tagElem = (
              <Tag
                key={annotation.id}
                closable
                style={{ userSelect: "none" }}
                color={annotation.annotationCategory?.color || "orange"}
                onClose={() =>
                  deleteAnnotation({
                    variables: {
                      annotationId: annotation.id,
                      connections: [flight.annotations?.__id || ""],
                    },
                    onCompleted: displayMutationResults(response => response.deleteAnnotation, {}),
                  })
                }
              >
                <span className="inline-block max-w-[20ch] truncate align-bottom">{tag}</span>
              </Tag>
            );

            return (
              <Popover
                content={<pre>{annotation.internalMetadata}</pre>}
                title={
                  <div className="flex justify-between">
                    <div>{tag}</div>
                    <div>
                      <Typography.Text type="secondary">
                        {annotation.author?.displayName}
                      </Typography.Text>
                    </div>
                  </div>
                }
                key={annotation.id}
              >
                {tagElem}
              </Popover>
            );
          })}
          <Tag style={tagPlusStyle} onClick={showInput}>
            <PlusOutlined /> Add Tag
          </Tag>
        </Space>
      ) : (
        <Suspense>
          <CategorySelector
            orgUuid={flight.organization?.uuid}
            displayOrgTags={flight.organization !== null}
            value={userFacingAnnotations.map(a => a.annotationCategory?.uuid || "")}
            onChange={onCategoriesSelected}
            multiSelect={true}
            allowCreate
            className="flex-auto"
            style={{ width: "100%" }}
            onBlur={() => setInputVisible(false)}
            allowClear={false}
            autoFocus
            defaultOpen
          />
        </Suspense>
      )}
      <Space size={[0, 8]} wrap>
        {systemAnnotations.map(annotation => {
          const tag = annotation.annotationCategory?.name || "";
          const tagElem = (
            <Tag key={annotation.id} closable={false} style={{ userSelect: "none" }}>
              <span className="inline-block max-w-[20ch] truncate align-bottom">{tag}</span>
            </Tag>
          );

          if (annotation.internalMetadata) {
            return (
              <Popover
                content={<pre>{annotation.internalMetadata}</pre>}
                title={tag}
                key={annotation.id}
              >
                {tagElem}
              </Popover>
            );
          }

          return tagElem;
        })}
      </Space>
    </Space>
  );
};

export default FlightAnnotations;
